import { bindFieldValue, fieldValuePresent } from '@lib/helpers/field-value'
import type { FieldDto, FieldValueDto } from '@lib/dto/envelope.dto'
import type { TimeZone } from '@lib/validators/tenant.validator'

/** Today's date as a date-signed field shows it: in the organization's time zone, as the server stamps it. */
export function signedOnLabel(timeZone: TimeZone, now = new Date()): string {
  return now.toLocaleDateString('en-US', { dateStyle: 'medium', timeZone })
}

/**
 * Date-signed fields are filled when the matching signature is applied.
 * Re-apply that after a draft reload so an empty date never blocks the signer.
 */
export function backfillSignedDates(
  fields: readonly FieldDto[],
  values: Record<string, FieldValueDto>,
  timeZone: TimeZone,
  now = new Date(),
): Record<string, FieldValueDto> {
  const signedKeys = new Set<string>()
  for (const field of fields) {
    const bound = bindFieldValue(field, values[field.id])
    if (bound.type === 'signature' && fieldValuePresent(bound.value)) {
      signedKeys.add(`${field.documentId}:${String(field.page)}`)
    }
  }
  if (signedKeys.size === 0) {
    return values
  }
  const signedOn = signedOnLabel(timeZone, now)
  let changed = false
  const next = { ...values }
  for (const field of fields) {
    const bound = bindFieldValue(field, next[field.id])
    if (
      bound.type !== 'date_signed' ||
      !signedKeys.has(`${field.documentId}:${String(field.page)}`) ||
      fieldValuePresent(bound.value)
    ) {
      continue
    }
    next[field.id] = signedOn
    changed = true
  }
  return changed ? next : values
}
